import React, { useState } from 'react'
import styled from '@emotion/styled'

import { colors } from 'Constants'
import { runOnEnter } from 'Helpers'

import { FadedText, Text } from 'Components/shared/Typography'
import { Flex } from 'lese'

import { ChevronDown } from 'react-feather'

import { Button } from 'Components/shared/Button'
import { ClickableLink } from 'Components/shared/Link'

const OptionsContainer = styled(Flex)`
  display: none;
  position: absolute;
  top: 100%;
  background: ${colors.background};
  width: 100%;
  border: 1px solid ${colors.borderColor};
  border-top: none;
  z-index: 10;
  ${({ height }) =>
    height &&
    `
  height: ${height};
  overflow-y: scroll;
  `}
`

const optionStyle = `
  cursor: pointer;
  padding: 12px;

  :hover {
    background-color: ${colors.background800};
  }
`

const Option = styled(Text)`
  ${optionStyle}
`

const MultiOption = styled(Flex)`
  ${optionStyle}
`

const MultiOptionText = styled(Text)`
  line-height: 16px;
`

const MultiOptionBox = styled.span`
  border: 1px solid ${colors.darkGreen};
  margin-right: 10px;
  height: 16px;
  width: 16px;
`

const MultiOptionBoxActive = styled(MultiOptionBox)`
  background-color: ${colors.darkGreen};
`

const MultiOptionClose = styled(ClickableLink)`
  text-decoration: underline;
`

const SelectContainer = styled(Flex)`
  position: relative;
  cursor: pointer;
  outline: 0;
  ${({ open }) =>
    open &&
    `${OptionsContainer} {
      display: flex;
    }

    .select-icon {
      transform: rotate(180deg);
    }`}
  ${({ background }) =>
    background &&
    `
  background: ${background};
  `}
`

const Select = ({
  options,
  header,
  height,
  width,
  minWidth,
  xAlign,
  onChange,
  background,
  selected
}) => {
  const [open, setOpen] = useState(false)

  const generateChangeHandler = (newValue, previousValue) => () => {
    if (newValue === previousValue) return
    if (typeof onChange === 'function') onChange({ newValue, previousValue })
  }

  options = options.map((option, idx) =>
    typeof option === 'string' ? { label: option, value: idx } : option
  )

  selected = options.filter(option => option.value === selected)
  selected = selected.length > 0 ? selected[0] : options[0]

  return (
    <SelectContainer
      tabIndex="0"
      style={{ width, minWidth }}
      xAlign={xAlign}
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      onClick={() => setOpen(!open)}
      open={open}
      background={background}
    >
      <Flex separation="4px" yAlign>
        <Text>{selected?.label}</Text>
        <ChevronDown
          className="select-icon"
          style={{ float: 'right', marginLeft: 10 }}
          size="16px"
        />
      </Flex>
      <OptionsContainer column height={height}>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map(option => (
          <Option onClick={generateChangeHandler(option.value, selected?.value)}>
            {option.label}
          </Option>
        ))}
      </OptionsContainer>
    </SelectContainer>
  )
}

const SelectUsingButton = ({ selection, onChange, options, header }) => {
  const [open, setOpen] = useState(false)

  const generateOnChangeHandler = i => () => onChange({ previousIndex: selection, newIndex: i })

  options = options.map(option => (typeof option === 'string' ? { label: option } : option))

  return (
    <SelectContainer
      tabIndex="0"
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      onClick={() => setOpen(!open)}
      open={open}
    >
      <Button separation="4px" yAlign color={options[selection].color} wide>
        <Text>{options[selection].label}</Text>
        <ChevronDown className="select-icon" size="16px" />
      </Button>
      <OptionsContainer column>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map((option, i) => (
          <Option onClick={generateOnChangeHandler(i)}>{option.label}</Option>
        ))}
      </OptionsContainer>
    </SelectContainer>
  )
}

const SelectMulti = ({
  options,
  title,
  header,
  height,
  width,
  minWidth,
  xAlign,
  onChange,
  background,
  selected,
  close
}) => {
  const [open, setOpen] = useState(false)

  const generateChangeHandler = newValue => () => {
    if (selected.indexOf(newValue) === -1) {
      selected.push(newValue)
    } else {
      selected.splice(selected.indexOf(newValue), 1)
    }

    if (typeof onChange === 'function') onChange({ newValue: selected })
  }

  options = options.map((option, idx) =>
    typeof option === 'string' ? { label: option, value: idx } : option
  )

  return (
    <SelectContainer
      tabIndex="0"
      style={{ width, minWidth }}
      xAlign={xAlign}
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      open={open}
      background={background}
    >
      <Flex separation="4px" yAlign onClick={() => setOpen(!open)}>
        <Text>{title}</Text>
        <ChevronDown
          className="select-icon"
          style={{ float: 'right', marginLeft: 10 }}
          size="16px"
        />
      </Flex>
      <OptionsContainer column height={height}>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map(option => {
          const isSelected = selected.includes(option.value)
          return (
            <MultiOption onClick={generateChangeHandler(option.value)} yAlign>
              {isSelected ? <MultiOptionBoxActive /> : <MultiOptionBox />}
              <MultiOptionText>{option.label}</MultiOptionText>
            </MultiOption>
          )
        })}
        {close && (
          <MultiOption onClick={() => setOpen(false)} xAlign="flex-end">
            <MultiOptionClose>Close</MultiOptionClose>
          </MultiOption>
        )}
      </OptionsContainer>
    </SelectContainer>
  )
}

export { Select, SelectUsingButton, SelectMulti }
